import { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, BookOpen, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default rules content
const defaultRulesContent = {
  en: {
    title: "Ladder Rules",
    intro: "A monthly ladder designed to encourage regular play, fair competition, and new partnerships.",
    basicsTitle: "Basics",
    basics: [
      "Players are responsible for booking courts and finding partners.",
      "Only matches played with 4 players participating in the ladder are counted.",
      "Match duration is 90 minutes.",
      "The ladder runs monthly and resets at the beginning of each month.",
      "Play with different partners to earn bonus points.",
      "Report the match score to the admins after the match."
    ],
    matchFormatTitle: "Match Format",
    matchFormat: [
      "Matches are played as best of 3 sets.",
      "A team must win 2 sets to win the match.",
      "Golden Point is played at 40–40 (no advantage).",
      "If a set reaches 6–6, a Tie Break is played to 7 points (win by 2).",
      "If sets are tied 1–1, a Super Tie Break is played to 10 points (win by 2)."
    ],
    scoringTitle: "Scoring",
    scoring: [
      "Win = 2 points",
      "Loss = 1 point",
      "Unique Partner = +1 point (awarded once per partner per month)"
    ],
    tiebreakerTitle: "Tie-breaker (Ranking)",
    tiebreakerText: "If players are tied in points, ranking is determined by game difference (games won − games lost). The Super Tie Break counts as one game.",
    footer: "Compete, but don't take yourself so seriously."
  },
  el: {
    title: "Κανόνες Ladder",
    intro: "Ένα μηνιαίο ladder σχεδιασμένο για να ενθαρρύνει το συχνό παιχνίδι, το fair play και τους νέους συμπαίκτες.",
    basicsTitle: "Βασικά",
    basics: [
      "Οι παίκτες είναι υπεύθυνοι για το κλείσιμο γηπέδου και την εύρεση συμπαικτών.",
      "Μετράνε μόνο αγώνες με 4 παίκτες που συμμετέχουν στο ladder.",
      "Το ladder τρέχει σε μηνιαία βάση και μηδενίζεται στην αρχή κάθε μήνα.",
      "Παίξτε με διαφορετικούς συμπαίκτες για να κερδίσετε bonus πόντους.",
      "Δηλώστε το σκορ στους admins μετά το τέλος του αγώνα."
    ],
    matchFormatTitle: "Μορφή Αγώνα",
    matchFormat: [
      "Οι αγώνες παίζονται ως best of 3 με μέγιστη διάρκεια 90 λεπτών.",
      "Μια ομάδα πρέπει να έχει 2 νικηφόρα σετ για να κερδίσει τον αγώνα.",
      "Στο 40–40 παίζεται Golden Point (χωρίς advantage).",
      "Αν το σετ φτάσει 6–6, παίζεται Tie Break στους 7 πόντους (διαφορά 2).",
      "Αν τα σετ είναι 1–1, παίζεται Super Tie Break στους 10 πόντους (διαφορά 2)."
    ],
    scoringTitle: "Βαθμολογία",
    scoring: [
      "Νίκη = 2 πόντοι",
      "Ήττα = 1 πόντος",
      "Μοναδικός συμπαίκτης = +1 πόντος (μετράει μία φορά ανά συμπαίκτη κάθε μήνα)"
    ],
    tiebreakerTitle: "Ισοβαθμία (Κατάταξη)",
    tiebreakerText: "Σε περίπτωση ισοβαθμίας, η κατάταξη καθορίζεται από τη διαφορά games (games won − games lost). Το Super Tie Break μετράει ως ένα game.",
    footer: "Παίξε ανταγωνιστικά, αλλά μην παίρνεις τον εαυτό σου τόσο στα σοβαρά."
  }
};

export default function AdminRulesEditor() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [rulesContent, setRulesContent] = useState(defaultRulesContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('en');

  const fetchRulesContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/rules-content`);
      if (res.ok) {
        const data = await res.json();
        // Merge with defaults to ensure all fields exist
        setRulesContent({
          en: { ...defaultRulesContent.en, ...(data.en || {}) },
          el: { ...defaultRulesContent.el, ...(data.el || {}) }
        });
      }
    } catch {
      // Use defaults on error
      setRulesContent(defaultRulesContent);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRulesContent();
  }, [fetchRulesContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/rules-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(rulesContent)
      });

      if (res.ok) {
        toast.success(language === 'el' ? 'Οι κανόνες αποθηκεύτηκαν' : 'Rules saved');
        setHasChanges(false);
      } else {
        throw new Error('Failed to save');
      }
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (lang, field, value) => {
    setRulesContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const updateArrayItem = (lang, field, index, value) => {
    setRulesContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: prev[lang][field].map((item, i) => i === index ? value : item)
      }
    }));
    setHasChanges(true);
  };

  const addArrayItem = (lang, field) => {
    setRulesContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: [...prev[lang][field], '']
      }
    }));
    setHasChanges(true);
  };

  const removeArrayItem = (lang, field, index) => {
    setRulesContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: prev[lang][field].filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="rules-editor-loading">
        <div className="animate-spin h-8 w-8 border-4 border-muted border-t-primary rounded-full" />
      </div>
    );
  }

  const renderLanguageEditor = (lang) => {
    const content = rulesContent[lang];
    const labels = lang === 'en' ? {
      title: 'Page Title',
      intro: 'Introduction',
      basicsTitle: 'Basics Section Title',
      basics: 'Basics Rules',
      matchFormatTitle: 'Match Format Section Title',
      matchFormat: 'Match Format Rules',
      scoringTitle: 'Scoring Section Title',
      scoring: 'Scoring Rules',
      tiebreakerTitle: 'Tiebreaker Section Title',
      tiebreakerText: 'Tiebreaker Text',
      footer: 'Footer Quote'
    } : {
      title: 'Τίτλος Σελίδας',
      intro: 'Εισαγωγή',
      basicsTitle: 'Τίτλος Ενότητας Βασικών',
      basics: 'Κανόνες Βασικών',
      matchFormatTitle: 'Τίτλος Ενότητας Μορφής Αγώνα',
      matchFormat: 'Κανόνες Μορφής Αγώνα',
      scoringTitle: 'Τίτλος Ενότητας Βαθμολογίας',
      scoring: 'Κανόνες Βαθμολογίας',
      tiebreakerTitle: 'Τίτλος Ενότητας Ισοβαθμίας',
      tiebreakerText: 'Κείμενο Ισοβαθμίας',
      footer: 'Φράση Footer'
    };

    return (
      <div className="space-y-6">
        {/* Title & Intro */}
        <div className="space-y-4">
          <div>
            <Label>{labels.title}</Label>
            <Input
              value={content.title}
              onChange={(e) => updateField(lang, 'title', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>{labels.intro}</Label>
            <Textarea
              value={content.intro}
              onChange={(e) => updateField(lang, 'intro', e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
        </div>

        {/* Basics Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="mb-3">
            <Label>{labels.basicsTitle}</Label>
            <Input
              value={content.basicsTitle}
              onChange={(e) => updateField(lang, 'basicsTitle', e.target.value)}
              className="mt-1"
            />
          </div>
          <Label className="text-sm text-muted-foreground">{labels.basics}</Label>
          <div className="space-y-2 mt-2">
            {content.basics.map((item, idx) => (
              <div key={`basics-${idx}`} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => updateArrayItem(lang, 'basics', idx, e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem(lang, 'basics', idx)}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => addArrayItem(lang, 'basics')}
              className="gap-1"
            >
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </div>

        {/* Match Format Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="mb-3">
            <Label>{labels.matchFormatTitle}</Label>
            <Input
              value={content.matchFormatTitle}
              onChange={(e) => updateField(lang, 'matchFormatTitle', e.target.value)}
              className="mt-1"
            />
          </div>
          <Label className="text-sm text-muted-foreground">{labels.matchFormat}</Label>
          <div className="space-y-2 mt-2">
            {content.matchFormat.map((item, idx) => (
              <div key={`matchFormat-${idx}`} className="flex gap-2">
                <span className="flex items-center justify-center w-6 h-9 text-sm font-medium text-muted-foreground">{idx + 1}.</span>
                <Input
                  value={item}
                  onChange={(e) => updateArrayItem(lang, 'matchFormat', idx, e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem(lang, 'matchFormat', idx)}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => addArrayItem(lang, 'matchFormat')}
              className="gap-1"
            >
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </div>

        {/* Scoring Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="mb-3">
            <Label>{labels.scoringTitle}</Label>
            <Input
              value={content.scoringTitle}
              onChange={(e) => updateField(lang, 'scoringTitle', e.target.value)}
              className="mt-1"
            />
          </div>
          <Label className="text-sm text-muted-foreground">{labels.scoring}</Label>
          <div className="space-y-2 mt-2">
            {content.scoring.map((item, idx) => (
              <div key={`scoring-${idx}`} className="flex gap-2">
                <Input
                  value={item}
                  onChange={(e) => updateArrayItem(lang, 'scoring', idx, e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem(lang, 'scoring', idx)}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => addArrayItem(lang, 'scoring')}
              className="gap-1"
            >
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </div>

        {/* Tiebreaker Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="mb-3">
            <Label>{labels.tiebreakerTitle}</Label>
            <Input
              value={content.tiebreakerTitle}
              onChange={(e) => updateField(lang, 'tiebreakerTitle', e.target.value)}
              className="mt-1"
            />
          </div>
          <Label>{labels.tiebreakerText}</Label>
          <Textarea
            value={content.tiebreakerText}
            onChange={(e) => updateField(lang, 'tiebreakerText', e.target.value)}
            rows={2}
            className="mt-1"
          />
        </div>

        {/* Footer */}
        <div>
          <Label>{labels.footer}</Label>
          <Input
            value={content.footer}
            onChange={(e) => updateField(lang, 'footer', e.target.value)}
            className="mt-1"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="admin-rules-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {language === 'el' ? 'Επεξεργαστής Κανόνων' : 'Rules Editor'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {language === 'el' ? 'Επεξεργασία κανόνων για κάθε γλώσσα' : 'Edit rules content for each language'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRulesContent}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            size="sm"
            data-testid="save-rules-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : t('save')}
          </Button>
        </div>
      </div>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-600 dark:text-amber-400 text-sm">
          {language === 'el' ? 'Έχετε μη αποθηκευμένες αλλαγές.' : 'You have unsaved changes.'}
        </div>
      )}

      {/* Language Tabs */}
      <div className="bg-card rounded-xl border border-border p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="en" className="gap-2">
              <span className="inline-flex items-center justify-center w-6 h-5 rounded bg-green-500/20 text-green-600 dark:text-green-400 text-xs font-bold">EN</span>
              English
            </TabsTrigger>
            <TabsTrigger value="el" className="gap-2">
              <span className="inline-flex items-center justify-center w-6 h-5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold">EL</span>
              Ελληνικά
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="en">
            {renderLanguageEditor('en')}
          </TabsContent>
          
          <TabsContent value="el">
            {renderLanguageEditor('el')}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
